import { FC, useMemo } from "react"
import { ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Bar, Legend, Tooltip, LegendProps } from "recharts"
import EnterpriseComplianceDashboardChartHeader from "../components/EcdChartHeader";
import { CustomXAxisTickOffsetX, customLegend, gapsByAgeCustomTooltip, getPercentChange, getYAxisWidth } from "../utility";
import { AdequacyBreakdownData, GapsByAgeData } from "../interfaces";

interface GapsByAgeChartProps {
  gapsByAgeData: GapsByAgeData[];
  adequacyBreakdownData: AdequacyBreakdownData;
  barHoverBackground?: string;
  gapsArray: string[];
  isExport: boolean;
}

const GapsByAgeChart: FC<GapsByAgeChartProps> = (props: GapsByAgeChartProps) => {

    const medianGapAgePercentChange = useMemo(() => {
        return getPercentChange(
            parseInt(props.adequacyBreakdownData?.medianGapAgePrior, 10),
            parseInt(props.adequacyBreakdownData?.medianGapAge, 10)
        );
    }, [props.adequacyBreakdownData]);

    const yAxisWidth = useMemo(() => {
        const values = props.gapsByAgeData?.map(
            (d) => (d.gapAge30 +
                d.gapAge60 +
                d.gapAge90 +
                d.gapAgeOver90)
        );
        return getYAxisWidth(values);
    }, [props.gapsByAgeData]);

    return (
        <>
            <div style={{
                bottom: "16px",
                display: "block",
                height: 0,
                left: "max(30px, 10%)",
                position: "relative",
                zIndex: 5, // in case it overlaps with the chart
            }}
            >
                <EnterpriseComplianceDashboardChartHeader
                    trendArrow={props.adequacyBreakdownData?.medianGapAgeTrend}
                    label="Median Gap Age in Days"
                    number={parseInt(props.adequacyBreakdownData?.medianGapAge, 10)}
                    percentage={medianGapAgePercentChange}
                />
            </div>
            <div style={{ height: "300px", width: "100%" }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        width={500}
                        height={400}
                        data={props.gapsByAgeData}
                    >
                        <CartesianGrid strokeDasharray="0 3" />
                        <XAxis
                            dataKey="month"
                            type="category"
                            height={32}
                            tickLine={false}
                            ticks={props.gapsByAgeData?.map((d) => d.month)}
                            tick={<CustomXAxisTickOffsetX xOffset={20} />}
                            interval={"preserveStartEnd"}
                        />
                        <YAxis
                            tickLine={false}
                            width={yAxisWidth}
                            tickSize={12}
                            allowDecimals={false}
                            tickFormatter={(tick: number) => tick.toLocaleString()}
                        />
                        {!props.isExport && (<Tooltip
                          content={(props) => gapsByAgeCustomTooltip(props, "month")}
                          cursor={{ fill: props.barHoverBackground }}
                          itemStyle={{ background: "white", color: "black" }}
                        />)}
                        <Bar
                            dataKey="gapAgeOver90"
                            fill={props.gapsArray[0]}
                            name="> 90 Days"
                            stackId="stack"
                        />
                        <Bar
                            dataKey="gapAge90"
                            fill={props.gapsArray[1]}
                            name="61 - 90 Days"
                            stackId="stack"
                        />
                        <Bar
                            dataKey="gapAge60"
                            fill={props.gapsArray[2]}
                            name="31 - 60 Days"
                            stackId="stack"
                        />
                        <Bar
                            dataKey="gapAge30"
                            fill={props.gapsArray[3]}
                            name="< 30 Days"
                            stackId="stack"
                        />
                        <Legend
                            content={(props) => customLegend(props as LegendProps)}
                            layout="vertical"
                            wrapperStyle={{
                                // the legend is default position absolute
                                bottom: "85%",
                                columns: 2,
                                left: "max(45%, 135px)",
                            }}
                            verticalAlign="top"
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </>
    )
};

export default GapsByAgeChart;
